// ─── conditionLibrary.js ──────────────────────────────────────────────────────
// Curated, offline phrase library for auto-generating Condition Report prose.
// NOT AI-generated at runtime — deterministic assembly from tapped defect tags.
// Voice mined from real Higgins condition reports. Every output is editable.

import { EXCLUDING_FINISHES, FINISH_BY_KEY, CONDITION_TO_RAG, RAG_RECOMMENDATION } from './areaConstants';

// Local label normaliser for substrate↔photo matching (case/space/dash-insensitive).
function normLabelLib(s) {
  return (s || '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/[‐-―]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// ── Substrate → material family ───────────────────────────────────────────────
// Drives which defect tags are offered for a given surface. Falls back to
// 'generic' (paint-film defects only) for unmapped/custom surfaces.
export const SUBSTRATE_FAMILY = {
  // Painted plasterboard / lined internal
  'Ceilings': 'lined',
  'Walls': 'lined',        // internal walls; external walls overridden below by areaType where needed
  'Skirting': 'timber',
  // Timber joinery
  'Doors': 'timber',
  'Windows': 'timber',
  'Stairs': 'timber',
  'Fascia': 'timber',      // many AU fascia are timber; metal handled by user via defect choice
  'Posts': 'timber',
  'Balustrade / Handrails': 'timber',
  'Play Equipment': 'timber',
  'Timber Play Equipment': 'timber',  // legacy key — pre-rename projects
  'Fences': 'timber',
  'Store Rooms': 'lined',
  // 'Line Marking' deliberately omitted → falls through to 'generic'
  //   (paint-film defects only: fading, wear, poor coverage — correct for line marking)
  // Eaves / soffit (usually fibre-cement or lined sheet)
  'Eaves': 'sheet',
  'Soffit': 'sheet',
  // Metal
  'Gutter': 'metal',
  'Downpipes': 'metal',
  'Roofs': 'metal',
  // Masonry / render / concrete
  'Floors': 'masonry',
  'Slab Edge': 'masonry',
};

// Resolve family for a substrate, honouring baseSurface (independent blocks) and areaType.
export function familyForSubstrate(substrate, baseSurface, areaType) {
  const key = baseSurface || substrate;
  // External "Walls" are commonly render/masonry; internal are lined plasterboard.
  if (key === 'Walls') return areaType === 'external' ? 'masonry' : 'lined';
  return SUBSTRATE_FAMILY[key] || 'generic';
}

// ── Defect definitions ────────────────────────────────────────────────────────
// Each defect: id, label (chip text), phrase (sentence fragment, lower-case verb
// phrase that follows the substrate), families it applies to ('generic' = all),
// and makeGood: true if it implies pre-paint repair / make-good.
//
// `phrase` is written to slot into: "[Substrate] [phrase] [extent]."
// e.g. Walls + "is peeling" + "in a large number of areas"
//   → "Walls is peeling in a large number of areas." (grammar smoothed in assembler)
export const DEFECTS = [
  // ── Universal paint-film defects (all families) ──────────────────────────
  { id: 'peeling',         label: 'Peeling / flaking',      phrase: 'paint is peeling and flaking',                         families: ['generic'], makeGood: false },
  { id: 'blistering',      label: 'Blistering / bubbling',  phrase: 'the coating is blistering and bubbling',               families: ['generic'], makeGood: false },
  { id: 'cracking_film',   label: 'Cracking (paint)',       phrase: 'the paint film is cracking',                           families: ['generic'], makeGood: false },
  { id: 'alligatoring',    label: 'Alligatoring',           phrase: 'the paint shows alligatoring / crocodiling',           families: ['generic'], makeGood: false },
  { id: 'chalking',        label: 'Chalking',               phrase: 'the surface is chalking',                              families: ['generic'], makeGood: false },
  { id: 'fading',          label: 'Fading / colour loss',   phrase: 'the colour has faded',                                 families: ['generic'], makeGood: false },
  { id: 'discolouration',  label: 'Discolouration',         phrase: 'the paint is discoloured',                             families: ['generic'], makeGood: false },
  { id: 'loss_gloss',      label: 'Loss of gloss',          phrase: 'the finish has lost its gloss',                        families: ['generic'], makeGood: false },
  { id: 'dirt_grime',      label: 'Dirt / grime',           phrase: 'the surface is heavily soiled with dirt and grime',    families: ['generic'], makeGood: false },
  { id: 'poor_coverage',   label: 'Poor coverage',          phrase: 'the previous coating has not covered adequately',      families: ['generic'], makeGood: false },
  { id: 'bleed_through',   label: 'Bleed-through',          phrase: 'the original colour is bleeding through',              families: ['generic'], makeGood: false },
  { id: 'holes_patching',  label: 'Holes / patching',       phrase: 'there are holes and patched areas requiring make good',families: ['generic'], makeGood: true },
  { id: 'mechanical_dmg',  label: 'Wear & tear / damage',   phrase: 'there are visible marks of wear and mechanical damage',families: ['generic'], makeGood: false },
  { id: 'poor_prep',       label: 'Poor prior prep',        phrase: 'the previous coating was applied without adequate preparation', families: ['generic'], makeGood: false },
  { id: 'delamination',    label: 'Delamination',           phrase: 'the coating is delaminating and lifting from the substrate', families: ['generic'], makeGood: false },
  { id: 'tacky',           label: 'Tacky / uncured',        phrase: 'the coating remains tacky and has not cured',          families: ['generic'], makeGood: false },
  { id: 'wrong_colour',    label: 'Wrong colour scheme',    phrase: 'the colour scheme does not match the current specification', families: ['generic'], makeGood: false },
  { id: 'never_painted',   label: 'Never painted / bare',   phrase: 'the substrate appears to have never been painted',     families: ['generic'], makeGood: false },
  { id: 'paint_failure',   label: 'General paint failure',  phrase: 'the paint has failed',                                 families: ['generic'], makeGood: false },
  { id: 'lap_marks',       label: 'Lap marks / uneven',     phrase: 'the finish is uneven with visible lap marks',          families: ['generic'], makeGood: false },

  // ── Biological (lined wet areas + all exterior families) ─────────────────
  { id: 'mould',           label: 'Mould / mildew',         phrase: 'mould and mildew are present',                         families: ['lined','sheet','timber','masonry','metal'], makeGood: true },
  { id: 'algae',           label: 'Algae / green staining', phrase: 'algae and green staining are present',                 families: ['sheet','timber','masonry','metal'], makeGood: true },
  { id: 'lichen',          label: 'Lichen growth',          phrase: 'lichen growth is established on the surface',          families: ['sheet','masonry','metal'], makeGood: true },

  // ── Timber-specific ──────────────────────────────────────────────────────
  { id: 'rot',             label: 'Timber rot / decay',     phrase: 'the timber is showing signs of rot and decay',         families: ['timber'], makeGood: true },
  { id: 'soft_grain',      label: 'Soft / spongy timber',   phrase: 'sections of timber have gone soft and require repair', families: ['timber'], makeGood: true },
  { id: 'splitting',       label: 'Splitting / cracking',   phrase: 'the timber is splitting and cracking',                 families: ['timber'], makeGood: true },
  { id: 'raised_grain',    label: 'Raised / weathered grain', phrase: 'the grain has raised and weathered',                 families: ['timber'], makeGood: false },
  { id: 'knot_bleed',      label: 'Knot / resin bleed',     phrase: 'knot and resin bleed is showing through the coating',  families: ['timber'], makeGood: false },
  { id: 'end_grain',       label: 'End-grain moisture',     phrase: 'moisture damage is evident at the end grain',          families: ['timber'], makeGood: true },
  { id: 'borer',           label: 'Insect / borer damage',  phrase: 'there is evidence of insect / borer damage',           families: ['timber'], makeGood: true },
  { id: 'warping',         label: 'Warping / cupping',      phrase: 'the timber has warped and cupped',                     families: ['timber'], makeGood: true },

  // ── Metal-specific ───────────────────────────────────────────────────────
  { id: 'surface_rust',    label: 'Surface rust',           phrase: 'surface rust and corrosion are present',               families: ['metal'], makeGood: true },
  { id: 'rust_perforation',label: 'Rust perforation',       phrase: 'corrosion has perforated the metal',                   families: ['metal'], makeGood: true },
  { id: 'oxidation',       label: 'Oxidation / chalked finish', phrase: 'the factory finish has oxidised and chalked',      families: ['metal'], makeGood: false },
  { id: 'salt_corrosion',  label: 'Salt / coastal corrosion', phrase: 'salt deposits and coastal corrosion are evident',    families: ['metal'], makeGood: true },
  { id: 'fastener_rust',   label: 'Fastener corrosion',     phrase: 'corroded fasteners are staining the surface',          families: ['metal'], makeGood: true },

  // ── Masonry / render / concrete ──────────────────────────────────────────
  { id: 'hairline_crack',  label: 'Hairline / map cracking',phrase: 'hairline and map cracking are present',                families: ['masonry'], makeGood: true },
  { id: 'structural_crack',label: 'Structural cracking',    phrase: 'structural cracking is evident and should be assessed',families: ['masonry'], makeGood: true },
  { id: 'efflorescence',   label: 'Efflorescence (salt)',   phrase: 'efflorescence (salt bloom) is present',                families: ['masonry'], makeGood: true },
  { id: 'spalling',        label: 'Spalling / concrete cancer', phrase: 'the surface is spalling and breaking away',        families: ['masonry'], makeGood: true },
  { id: 'drummy_render',   label: 'Drummy / hollow render', phrase: 'sections of render are drummy and delaminating',       families: ['masonry'], makeGood: true },
  { id: 'rising_damp',     label: 'Rising damp / moisture', phrase: 'moisture staining consistent with rising damp is visible', families: ['masonry'], makeGood: true },

  // ── Sheet (fibre-cement eaves/soffit) ────────────────────────────────────
  { id: 'sheet_cracking',  label: 'Sheet cracking',         phrase: 'the sheeting is cracking',                             families: ['sheet'], makeGood: true },
  { id: 'sheet_joint',     label: 'Failed joints / stopping', phrase: 'the sheet joints and stopping have failed',          families: ['sheet'], makeGood: true },
  { id: 'sagging_sheet',   label: 'Sagging / water damage',  phrase: 'the sheeting is sagging from water damage',           families: ['sheet'], makeGood: true },
];

export const DEFECT_BY_ID = Object.fromEntries(DEFECTS.map(d => [d.id, d]));

// Defects offered for a given family: family-specific first, then universal.
export function defectsForFamily(family) {
  const specific = DEFECTS.filter(d => d.families.includes(family) && !d.families.includes('generic'));
  const universal = DEFECTS.filter(d => d.families.includes('generic'));
  return [...specific, ...universal];
}

// ── Vocabulary (mined from real reports) ──────────────────────────────────────
// Extent describes how widespread; severity frames the recommendation by RAG.
export const EXTENT_OPTIONS = [
  { key: 'isolated',   label: 'Isolated areas',   phrase: 'in isolated areas' },
  { key: 'some',       label: 'Some areas',       phrase: 'in some areas' },
  { key: 'multiple',   label: 'Multiple areas',   phrase: 'in multiple areas' },
  { key: 'widespread', label: 'Widespread',       phrase: 'in a large number of areas' },
  { key: 'throughout', label: 'Throughout',       phrase: 'throughout' },
];

// RAG-driven closing recommendation. Reads the single source of truth in
// areaConstants.js so this sentence always matches the hero/dashboard/register —
// the report never presents more than one recommendation vocabulary.
function severityCloser(condition) {
  const rag = CONDITION_TO_RAG[condition];
  return rag ? RAG_RECOMMENDATION[rag].closer : '';
}

const MAKEGOOD_CLOSER = 'Pre-paint repair and make good is required prior to the application of the new coating system.';

// Prose fragments for the P3 finish lead / scheme clause. Deliberately separate
// from FINISH_BY_KEY's chip label — "prefinished Colorbond" reads as a sentence,
// "Prefinished / Colorbond" does not.
const FINISH_LEAD_PHRASE = {
  prefinished: 'prefinished Colorbond',
  unpainted: 'unpainted / bare',
  galvanised: 'galvanised / mill finish',
};
const CONDITION_PHRASE = { poor: 'poor condition', fair: 'fair condition', good: 'good condition' };
const SCHEME_CLAUSE = ' They are not in the current colour scheme and are recommended for repaint per specification.';

// True when an entry has no defect tags at all (built-in or custom).
function hasNoDefects(entry) {
  const defectIds = Array.isArray(entry.defects) ? entry.defects : [];
  const customs = Array.isArray(entry.customDefects) ? entry.customDefects.filter((s) => s && s.trim()) : [];
  return defectIds.length === 0 && customs.length === 0;
}

// ── Substrate outcome resolution (P3) ──────────────────────────────────────────
// Resolve what a rated substrate actually MEANS, combining condition, finish and
// scheme compliance. Every consumer (register, RAG rollups, the report card) reads
// this instead of re-deriving the rules. Returns one of:
//   { outcome: 'excluded', reason: '<Finish label> — no work required' }
//   { outcome: 'scheme',   reason: 'Sound coating, not to current colour scheme' }
//   { outcome: 'condition' }   ← normal RAG-driven path (also the back-compat default
//                                 for an entry with neither finish nor schemeCompliant set)
export function resolveSubstrateOutcome(entry) {
  const e = entry || {};
  const noDefects = hasNoDefects(e);

  // A prefinished/unpainted/galvanised substrate with nothing wrong with it is
  // permanently out of scope — UNLESS it's also flagged out of colour scheme, in
  // which case it still gets repainted (a prefinished gutter that's the wrong
  // colour is not "no work required").
  if (EXCLUDING_FINISHES.has(e.finish) && noDefects && e.schemeCompliant !== false) {
    const label = (FINISH_BY_KEY[e.finish] && FINISH_BY_KEY[e.finish].label) || 'Prefinished';
    return { outcome: 'excluded', reason: `${label} — no work required` };
  }

  // Sound coating (good, no defects) but the client's colour spec has moved on —
  // this is a scheme problem, not a condition problem, so it should not read as
  // damage in the generated prose.
  if (e.schemeCompliant === false && e.condition === 'good' && noDefects) {
    return { outcome: 'scheme', reason: 'Sound coating, not to current colour scheme' };
  }

  return { outcome: 'condition' };
}

// ── The assembler ─────────────────────────────────────────────────────────────
// Builds a Condition Report sentence from a per-substrate entry.
// entry: { condition, defects:[ids], extent?:key, note? }
// Returns a string. Pure function — deterministic, no side effects.
export function generateConditionNote(substrate, entry) {
  if (!entry) return '';
  const condition = entry.condition || null;
  const defectIds = Array.isArray(entry.defects) ? entry.defects : [];
  const { outcome } = resolveSubstrateOutcome(entry);

  // Excluded (P3): lead with what the substrate IS, since that's why no work is
  // recommended — not because of its condition. resolveSubstrateOutcome only
  // returns 'excluded' when there are no tagged defects, so this always wins over
  // the defect-clause path below.
  if (outcome === 'excluded') {
    const phrase = FINISH_LEAD_PHRASE[entry.finish] || 'a prefinished finish';
    const condWord = CONDITION_PHRASE[condition] || 'serviceable condition';
    return `${substrate} is ${phrase} and in ${condWord}. No work is required.`;
  }

  // Good + no defects → standard "no work" line. resolveSubstrateOutcome only
  // returns 'scheme' when condition is 'good' with no defects, so this is the only
  // branch that can ever append the scheme clause.
  if (condition === 'good' && defectIds.length === 0) {
    const scheme = outcome === 'scheme' ? SCHEME_CLAUSE : '';
    return `${substrate} — ${severityCloser('good')}${scheme}`;
  }
  if (defectIds.length === 0 && (!Array.isArray(entry.customDefects) || entry.customDefects.filter(s => s && s.trim()).length === 0)) {
    // Rating but no defects tagged: minimal sentence + closer.
    return condition ? severityCloser(condition) : '';
  }

  const defs = defectIds.map(id => DEFECT_BY_ID[id]).filter(Boolean);
  const customPhrases = Array.isArray(entry.customDefects) ? entry.customDefects.filter(s => s && s.trim()) : [];
  const extent = (EXTENT_OPTIONS.find(e => e.key === entry.extent) || null);
  const extentPhrase = extent ? ` ${extent.phrase}` : '';

  // Compose the defect clause(s). First defect leads with the substrate as subject;
  // additional defects are joined naturally.
  const phrases = [...defs.map(d => d.phrase), ...customPhrases.map(s => s.trim())];
  let body;
  if (phrases.length === 1) {
    body = `${substrate}: ${phrases[0]}${extentPhrase}.`;
  } else {
    const last = phrases[phrases.length - 1];
    const head = phrases.slice(0, -1).join(', ');
    body = `${substrate}: ${head} and ${last}${extentPhrase}.`;
  }
  // Capitalise first letter of the clause after the colon for readability.
  body = body.replace(/: ([a-z])/, (m, c) => `: ${c.toUpperCase()}`);

  // Closers: make-good first (if any tagged defect needs it), then severity.
  const needsMakeGood = defs.some(d => d.makeGood);
  const closers = [];
  if (needsMakeGood) closers.push(MAKEGOOD_CLOSER);
  if (condition) {
    const closer = severityCloser(condition);
    if (closer) closers.push(closer);
  }

  return [body, ...closers].join(' ').trim();
}

// ─── Condition Report aggregation (Stage C) ───────────────────────────────────
// The Condition Report describes the SITE substrate-by-substrate (not per area).
// App substrates are aggregated across all areas into the template's fixed rows.

// DEPRECATED — exists solely to serve WordExport.js, which is not imported by any
// module. The live export (conditionReportV3.js) reads substrate names straight off
// conditionBySubstrate via buildRegister, so this mapping is not extended for new
// substrates (Store Rooms, Line Marking, Fences, Play Equipment).
//
// Map app substrate names → the template's fixed report-row labels.
// Multiple app substrates can feed one report row (worst-condition-wins, notes merged).
// Keys are matched case-insensitively against app substrate names AND baseSurface.
export const SUBSTRATE_TO_REPORT_ROW = {
  internal: {
    'Ceilings': 'Ceilings',
    'Walls': 'Walls',
    'Doors': 'Woodwork – Doors/ Frames',
    'Windows': 'Woodwork – Doors/ Frames',
    'Skirting': 'Skirtings',
    // "Store Rooms" has no direct app substrate — filled only if a custom surface maps to it.
  },
  external: {
    'Fascia': 'Fascias',
    'Gutter': 'Gutters & Downpipes',
    'Downpipes': 'Gutters & Downpipes',
    'Eaves': 'Eave / Soffits (veranda timber rafters)',
    'Soffit': 'Eave / Soffits (veranda timber rafters)',
    'Walls': 'Walls',
    'Doors': 'Doors',
    'Posts': 'Posts (Inc Shade Sail Posts)',
    // "Line Marking" — filled only if a custom surface maps to it.
  },
};

const CONDITION_RANK = { poor: 3, fair: 2, good: 1 };

// Pick the worse of two condition keys (poor beats fair beats good).
function worseCondition(a, b) {
  if (!a) return b;
  if (!b) return a;
  return (CONDITION_RANK[a] || 0) >= (CONDITION_RANK[b] || 0) ? a : b;
}

// DEPRECATED — exists solely to serve WordExport.js, which is not imported by any
// module. The live export is conditionReportV3.js.
//
// Aggregate condition data across all areas of one type ('internal'|'external')
// into report rows. Returns an ordered array:
//   [{ rowLabel, condition, note, photo: {url|null}, substrates:[...] }]
// `photo` is the hero/first defect photo found among contributing areas for that row.
export function aggregateConditionRows(areas, areaType) {
  const map = SUBSTRATE_TO_REPORT_ROW[areaType] || {};
  // rowLabel → accumulator
  const acc = {};
  const order = [];

  (areas || []).filter(a => a.type === areaType).forEach(area => {
    const cbs = area.conditionBySubstrate || {};
    Object.keys(cbs).forEach(sub => {
      const entry = cbs[sub];
      if (!entry || (!entry.condition && !(entry.note))) return;
      // Resolve which report row this substrate feeds. Honour baseSurface (independent blocks).
      const settings = area.surfaceSettings?.[sub] || {};
      const base = settings.baseSurface || sub;
      const rowLabel = map[sub] || map[base] || sub; // unmapped → its own row label
      if (!acc[rowLabel]) {
        acc[rowLabel] = { rowLabel, condition: null, notes: [], photos: [], substrates: [] };
        order.push(rowLabel);
      }
      const a2 = acc[rowLabel];
      a2.condition = worseCondition(a2.condition, entry.condition || null);
      if (entry.note && entry.note.trim()) a2.notes.push(entry.note.trim());
      if (!a2.substrates.includes(sub)) a2.substrates.push(sub);
      // Attributed photos ONLY: collect every photo in this area tagged to this
      // substrate (or its baseSurface). No guessing — untagged photos never appear.
      const areaPhotos = area.photos || [];
      const tagged = areaPhotos.filter(p =>
        (p.url || p.thumbnailUrl) &&
        (normLabelLib(p.substrate) === normLabelLib(sub) || normLabelLib(p.substrate) === normLabelLib(base))
      );
      // primary first (isPrimaryForSubstrate), else preserve encounter order
      tagged.sort((x, y) => (y.isPrimaryForSubstrate === true) - (x.isPrimaryForSubstrate === true));
      for (const p of tagged) {
        const url = p.url || p.thumbnailUrl;
        if (!a2.photos.some(q => q.url === url)) a2.photos.push({ url });
      }
    });
  });

  return order.map(label => {
    const a = acc[label];
    return {
      rowLabel: label,
      condition: a.condition,
      note: a.notes.join(' '),
      photo: a.photos[0] || null,          // primary → row cell
      additionalPhotos: a.photos.slice(1), // extras → appendix
      substrates: a.substrates,
    };
  });
}
